import React, { useState, useEffect } from 'react';
import { 
  Vote, Users, Award, Calendar, ChevronRight, Check, 
  Trophy, Clock, Star, User, Shield, Briefcase
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { format, formatDistanceToNow, parseISO, isBefore, isAfter } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Elections = () => {
  const { user, token } = useAuth();
  const [elections, setElections] = useState([]);
  const [currentElection, setCurrentElection] = useState(null);
  const [positions, setPositions] = useState(null);
  const [boardMembers, setBoardMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('current');
  const [nominateDialog, setNominateDialog] = useState({ open: false, position: null });
  const [myVotes, setMyVotes] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (currentElection && user && token) {
      fetchMyVotes();
    }
  }, [currentElection, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [electionsRes, positionsRes, boardRes, currentRes] = await Promise.all([
        axios.get(`${API_URL}/api/elections/`),
        axios.get(`${API_URL}/api/elections/positions`),
        axios.get(`${API_URL}/api/elections/board/current`),
        axios.get(`${API_URL}/api/elections/current`)
      ]);
      
      setElections(electionsRes.data);
      setPositions(positionsRes.data);
      setBoardMembers(boardRes.data);
      setCurrentElection(currentRes.data);
    } catch (error) {
      console.error('Error fetching election data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyVotes = async () => {
    if (!currentElection || !token) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/elections/${currentElection.id}/my-votes`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const votesMap = {};
      response.data.forEach(v => {
        const key = `${v.position_type}_${v.professional_group || ''}`;
        votesMap[key] = v.candidate_id;
      });
      setMyVotes(votesMap);
    } catch (error) {
      console.error('Error fetching votes:', error);
    }
  };

  const handleNominate = async (nomineeId, statement) => {
    if (!nominateDialog.position || !currentElection) return;
    
    try {
      await axios.post(
        `${API_URL}/api/elections/${currentElection.id}/nominate`,
        {
          election_id: currentElection.id,
          position_type: nominateDialog.position.type,
          nominee_id: nomineeId,
          statement: statement,
          professional_group: nominateDialog.position.professional_group
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Nomination submitted successfully!');
      setNominateDialog({ open: false, position: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit nomination');
    }
  };

  const handleVote = async (candidateId, positionType, professionalGroup) => {
    if (!currentElection) return;
    
    try {
      await axios.post(
        `${API_URL}/api/elections/${currentElection.id}/vote`,
        {
          election_id: currentElection.id,
          position_type: positionType,
          candidate_id: candidateId,
          professional_group: professionalGroup
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Vote recorded successfully!');
      fetchMyVotes();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cast vote');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Upcoming' },
      nomination: { color: 'bg-blue-100 text-blue-800', label: 'Nominations Open' },
      voting: { color: 'bg-green-100 text-green-800', label: 'Voting Open' },
      closed: { color: 'bg-purple-100 text-purple-800', label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };
    const v = variants[status] || variants.draft;
    return <Badge className={v.color}>{v.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-2">
            <Vote className="w-8 h-8 text-primary" />
            Board Elections
          </h1>
          <p className="text-muted-foreground mt-1">
            Nominate candidates and vote for your community leadership
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="current">Current Election</TabsTrigger>
            <TabsTrigger value="board">Current Board</TabsTrigger>
            <TabsTrigger value="history">Election History</TabsTrigger>
          </TabsList>

          {/* Current Election Tab */}
          <TabsContent value="current">
            {!currentElection ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Active Election</h3>
                  <p className="text-muted-foreground">
                    There is no election currently in progress. Check back later for the next election cycle.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Election Info Card */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {currentElection.title}
                          {getStatusBadge(currentElection.status)}
                        </CardTitle>
                        <CardDescription>{currentElection.description}</CardDescription>
                      </div>
                      <Badge variant="outline" className="text-lg px-4 py-2">
                        {currentElection.year}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                          <Users className="w-4 h-4" />
                          Nomination Period
                        </div>
                        <p className="font-medium">
                          {format(parseISO(currentElection.nomination_start), 'MMM d')} - {format(parseISO(currentElection.nomination_end), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                          <Vote className="w-4 h-4" />
                          Voting Period
                        </div>
                        <p className="font-medium">
                          {format(parseISO(currentElection.voting_start), 'MMM d')} - {format(parseISO(currentElection.voting_end), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Standard Positions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Executive Positions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {positions?.standard_positions.map(pos => {
                      const posCandidates = currentElection.positions?.find(p => p.type === pos.type)?.candidates || [];
                      const voteKey = `${pos.type}_`;
                      const hasVoted = myVotes[voteKey];
                      
                      return (
                        <div key={pos.type} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-semibold">{pos.title}</h4>
                              <p className="text-sm text-muted-foreground">{pos.description}</p>
                            </div>
                            {currentElection.status === 'nomination' && user && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setNominateDialog({ open: true, position: pos })}
                              >
                                Nominate
                              </Button>
                            )}
                          </div>
                          
                          {posCandidates.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic">No candidates yet</p>
                          ) : (
                            <div className="space-y-2">
                              {posCandidates.map(candidate => (
                                <div 
                                  key={candidate.id}
                                  className={`flex items-center justify-between p-3 rounded-lg ${
                                    hasVoted === candidate.id ? 'bg-primary/10 border-primary border' : 'bg-muted/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <Avatar>
                                      <AvatarFallback>
                                        {(candidate.nominee_display_name || candidate.nominee_username)[0].toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium">{candidate.nominee_display_name || candidate.nominee_username}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {candidate.endorsements_count} endorsements
                                        {currentElection.status === 'closed' && ` • ${candidate.vote_count || 0} votes`}
                                      </p>
                                    </div>
                                  </div>
                                  
                                  {currentElection.status === 'voting' && user && !hasVoted && (
                                    <Button 
                                      size="sm"
                                      onClick={() => handleVote(candidate.id, pos.type, null)}
                                    >
                                      Vote
                                    </Button>
                                  )}
                                  {hasVoted === candidate.id && (
                                    <Badge className="bg-green-100 text-green-800">
                                      <Check className="w-3 h-3 mr-1" /> Voted
                                    </Badge>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                {/* Board Member Positions by Group */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase className="w-5 h-5" />
                      Advisory Board Members (9 seats)
                    </CardTitle>
                    <CardDescription>
                      Each professional group elects one representative to the advisory board
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-4">
                      {positions?.professional_groups.map(group => {
                        const groupCandidates = currentElection.positions?.find(
                          p => p.type === 'board_member' && p.professional_group === group.id
                        )?.candidates || [];
                        const voteKey = `board_member_${group.id}`;
                        const hasVoted = myVotes[voteKey];
                        
                        return (
                          <div key={group.id} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium">{group.name}</h5>
                              {currentElection.status === 'nomination' && user && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => setNominateDialog({ 
                                    open: true, 
                                    position: { type: 'board_member', title: `Board Member - ${group.name}`, professional_group: group.id }
                                  })}
                                >
                                  +
                                </Button>
                              )}
                            </div>
                            
                            {groupCandidates.length === 0 ? (
                              <p className="text-xs text-muted-foreground italic">No candidates</p>
                            ) : (
                              <div className="space-y-2">
                                {groupCandidates.slice(0, 3).map(candidate => (
                                  <div 
                                    key={candidate.id}
                                    className={`p-2 rounded text-sm ${
                                      hasVoted === candidate.id ? 'bg-primary/10' : 'bg-muted/50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span>{candidate.nominee_display_name || candidate.nominee_username}</span>
                                      {currentElection.status === 'voting' && user && !hasVoted && (
                                        <Button 
                                          size="sm" 
                                          variant="ghost"
                                          className="h-6 px-2"
                                          onClick={() => handleVote(candidate.id, 'board_member', group.id)}
                                        >
                                          Vote
                                        </Button>
                                      )}
                                      {hasVoted === candidate.id && (
                                        <Check className="w-4 h-4 text-green-600" />
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Current Board Tab */}
          <TabsContent value="board">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {boardMembers.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center">
                    <Users className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Board Members Yet</h3>
                    <p className="text-muted-foreground">
                      Board members will appear here after the first election is finalized.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                boardMembers.map(member => (
                  <Card key={member.id}>
                    <CardContent className="p-4 text-center">
                      <Avatar className="w-16 h-16 mx-auto mb-3">
                        <AvatarFallback className="text-lg">
                          {(member.display_name || member.username)[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <h4 className="font-semibold">{member.display_name || member.username}</h4>
                      <Badge className="mt-2">{member.position_title}</Badge>
                      {member.professional_group && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Representing: {member.professional_group}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        Since {format(parseISO(member.term_start), 'MMM yyyy')}
                      </p>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Election History Tab */}
          <TabsContent value="history">
            <div className="space-y-4">
              {elections.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Calendar className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Past Elections</h3>
                    <p className="text-muted-foreground">
                      Election history will appear here after elections are completed.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                elections.map(election => (
                  <Card key={election.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{election.title}</h4>
                            {getStatusBadge(election.status)}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {format(parseISO(election.voting_start), 'MMMM d')} - {format(parseISO(election.voting_end), 'MMMM d, yyyy')}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm">
                          View Results <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Nominate Dialog */}
        <NominateDialog
          open={nominateDialog.open}
          onOpenChange={(open) => setNominateDialog({ ...nominateDialog, open })}
          position={nominateDialog.position}
          token={token}
          user={user}
          onSubmit={handleNominate}
        />
      </div>
    </div>
  );
};

// Nominate Dialog Component
const NominateDialog = ({ open, onOpenChange, position, token, user, onSubmit }) => {
  const [nomineeId, setNomineeId] = useState('');
  const [statement, setStatement] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const handleSelfNominate = () => {
    if (user) {
      setNomineeId(user.id);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nomineeId) {
      toast.error('Please select a nominee');
      return;
    }
    
    setLoading(true);
    await onSubmit(nomineeId, statement);
    setLoading(false);
    setNomineeId('');
    setStatement('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nominate for {position?.title}</DialogTitle>
          <DialogDescription>
            Submit a nomination for this position. You can nominate yourself or another member.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nominee</Label>
            <div className="flex gap-2">
              <Button 
                type="button" 
                variant={nomineeId === user?.id ? 'default' : 'outline'}
                onClick={handleSelfNominate}
                className="flex-1"
              >
                <User className="w-4 h-4 mr-2" />
                Nominate Myself
              </Button>
            </div>
            {nomineeId === user?.id && (
              <p className="text-sm text-muted-foreground">
                You are nominating yourself for {position?.title}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Nomination Statement (optional)</Label>
            <Textarea
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              placeholder="Why should this person be elected?"
              rows={3}
              maxLength={2000}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !nomineeId}>
              {loading ? 'Submitting...' : 'Submit Nomination'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Elections;
